import { useCallback, useMemo, useState } from "react";
import { MULTI_SEARCH_DELIMITER } from "src/constants";
import { debounce } from "src/core/utils/utils";

export function useDataTableControlFilter<T>(
  baseList: T[] | undefined,
  filterProperties: string[],
  filterFunction?: (filter: string, item: T) => boolean,
) {
  const [filterValue, setFilterValue] = useState<string>();
  const [secondaryFilterValue, setSecondaryFilterValue] =
    useState<boolean>(false);

  const [filteredList, setFilteredList] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useMemo(() => {
    if (baseList) {
      setFilteredList(baseList);
      setLoading(false);
    }
  }, [baseList]);

  const parsedProps = useMemo(
    () => filterProperties.map((prop) => prop.split(".")),
    [filterProperties],
  );

  /**
   * Clears filters
   */
  const clearFilter = useCallback(() => {
    setFilterValue(undefined);
    setSecondaryFilterValue(false);
  }, []);

  const checkIfFitsFilter = useCallback(
    (item: T, filterValue: string) => {
      const matchesProperty = parsedProps.some((path) => {
        let value: any = item;

        for (const key of path) {
          value = value?.[key];
        }

        return (
          typeof value === "string" && value.toLowerCase().includes(filterValue)
        );
      });

      return matchesProperty || (filterFunction?.(filterValue, item) ?? false);
    },
    [parsedProps, filterFunction],
  );

  const onFilterChange = useCallback(
    (
      filterString?: string,
      secondaryFilterProperty?: string,
      secondaryFilterValue?: boolean,
    ) => {
      setFilterValue(filterString);

      if (!baseList) return;

      const filters = filterString
        ?.split(MULTI_SEARCH_DELIMITER)
        .map((f) => f.trim().toLowerCase())
        .filter(Boolean);

      const hasTextFilters = !!filters?.length;
      const hasSecondaryFilter =
        !!secondaryFilterValue && !!secondaryFilterProperty;

      const result = baseList.filter((item) => {
        // text filters (AND)
        if (hasTextFilters) {
          const matchesAll = filters!.every((filterValue) =>
            checkIfFitsFilter(item, filterValue),
          );
          if (!matchesAll) return false;
        }

        // secondary filter
        if (hasSecondaryFilter) {
          return Boolean((item as any)[secondaryFilterProperty!]);
        }

        return true;
      });

      setFilteredList(result);
    },
    [baseList, checkIfFitsFilter],
  );

  const debouncedOnFilterChange = useMemo(
    () => debounce(onFilterChange, 100),
    [onFilterChange],
  );

  const onSecondaryFilterPropertyChange = useCallback(
    (property: string, value?: boolean, filterValue?: string) => {
      if (value) {
        if (!filteredList) return;

        const result = filteredList.filter((item) =>
          Boolean((item as any)[property]),
        );

        setFilteredList(result);
      } else {
        onFilterChange(filterValue);
      }
    },
    [filteredList, onFilterChange],
  );

  return {
    onFilterChange,
    debouncedOnFilterChange,
    filteredList,
    setFilteredList,
    onSecondaryFilterPropertyChange,
    loading,
    filterValue,
    setFilterValue,
    secondaryFilterValue,
    setSecondaryFilterValue,
    clearFilter,
  };
}
